<template>
  <div class="p-4 w-full mx-auto">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <div class="flex items-center">
          <Megaphone class="w-5 h-5 mr-1 text-gray-700" />
          <h1 class="text-xl font-normal text-gray-800">
            Warnings & Reports
          </h1>
          <span class="ml-2 text-sm text-gray-500">[{{ totalCount }}]</span>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <!-- Filter Buttons -->
        <div class="flex items-center gap-2">
          <button 
            class="text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="filterType === 'all' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="setFilter('all')"
          >
            All
          </button>
          <button 
            class="flex items-center text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="filterType === 'reports' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="setFilter('reports')"
          >
            <span class="w-1.5 h-1.5 bg-amber-500 mr-1.5 rounded-full"></span>
            Reports
          </button>
          <button 
            class="flex items-center text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="filterType === 'strikes' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="setFilter('strikes')"
          >
          <span class="w-1.5 h-1.5 bg-red-500 mr-1.5 rounded-full"></span>
            Strikes
          </button>
        </div>

        <!-- Search Bar -->
        <div class="relative w-[300px]">
          <Search class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search reports or warnings..."
            class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            v-model="searchQuery"
            @input="handleSearch"
          />
        </div>

        <button 
          class="text-xs flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-400 transition-all duration-200"
          @click="isStrikeModalOpen = true"
        >
          <Plus class="w-4 h-4" />
          Issue Warning
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-8 min-h-[calc(100vh-8rem)]">
      <OuttalentLoader full-title />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
      <p class="text-red-600">{{ error }}</p>
    </div>

    <div v-else>
      <!-- Reports Table -->
      <div class="bg-white">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Type
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  User
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Count
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Reason
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Status
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200 w-32"
                >
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="report in filteredReports" 
                :key="report.id" 
                class="hover:bg-gray-50 cursor-pointer"
                @click="selectedReport = report"
              >
                <!-- Type Column -->
                <td class="py-3 px-1 border border-gray-100 text-center">
                  <span
                    v-if="report.is_strike && report.resulted_in_suspension"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-black text-white"
                  >
                    Suspension
                  </span>
                  <span
                    v-else-if="report.is_strike && !report.is_revoked_strike"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                  >
                    Warning
                  </span>

                  <span
                    v-else-if="report.is_revoked_strike"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    Revoked
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                  >
                    Report
                  </span>
                </td>

                <!-- User Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div class="flex items-center gap-3">
                    <div v-if="report.user_details?.avatar_image" class="w-8 h-8 rounded-full overflow-hidden">
                      <img
                        :src="report.user_details.avatar_image"
                        :alt="`${report.user_details.first_name} ${report.user_details.last_name}`"
                        class="w-full h-full object-cover"
                        :class="{ 'blur-md select-none': isBlurred }"
                      />
                    </div>
                    <div
                      class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
                      v-else
                    >
                      <div class="w-full h-full flex items-center justify-center" :class="{ 'blur-md select-none': isBlurred }">
                        <User class="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900" :class="{ 'blur-md select-none': isBlurred }">
                        {{ report.user_details.first_name }} {{ report.user_details.last_name }}
                      </div>
                      <div class="text-xs text-gray-400">{{ report.fellow_data?.batch ? formatExpeditionName(report.fellow_data.batch) : 'No batch assigned' }}</div>
                    </div>
                  </div>
                </td>

                <!-- Count Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div class="flex items-center justify-center gap-1">
                    <Flag
                      v-for="n in report.strike_count"
                      :key="n"
                      class="w-4 h-4 text-yellow-500"
                    />
                  </div>
                </td>

                <!-- Reason Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <component
                        :is="reportHelpers.getReasonIcon(report.reason)"
                        class="w-3 h-3 mr-1 text-red-500"
                      />

                      {{ report.reason_display }}
                    </span>
                    <div
                      v-if="report.description"
                      class="text-xs text-gray-500 mt-1 max-w-xs truncate"
                      :title="report.description"
                    >
                      {{ report.description }}
                    </div>
                  </div>
                </td>

                <!-- Status Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <span
                    :class="reportHelpers.getStatusClass(report.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs"
                  >
                    {{ report.status_display }}
                  </span>
                </td>

                <!-- Date Column -->
                <td class="py-3 px-4 border border-gray-100 w-32">
                  <div class="text-xs text-gray-900">{{ formatDateUtil(report.created_at) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="filteredReports.length === 0 && !loading" class="text-center py-12">
          <div class="text-gray-500">No reports found for this expedition</div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6">
        <PaginationComponent
          :current-page="currentPage"
          :total-pages="totalPages"
          :use-router="true"
          @update:page="handlePageChange"
          class="mb-0"
        />
      </div>
    </div>

    <!-- Report Details Modal -->
    <ReportDetailsModal
      v-if="selectedReport"
      :selected-report="selectedReport"
      @close="selectedReport = null"
      @navigate-to-report="navigateToReport"
      @report-updated="handleReportUpdate"
    />

    <!-- Issue Warning Modal -->
    <IssueStrikeModal
      v-if="isStrikeModalOpen"
      :is-open="isStrikeModalOpen"
      @close="isStrikeModalOpen = false"
      @submitted="handleStrikeSubmitted"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import { Flag, Megaphone, User, Plus, Search } from 'lucide-vue-next'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import type { Report, FellowReport } from '@/types/reports'
import { useReportHelpers } from '@/composables/useReportHelpers'
import { useDebounce } from '@/composables/useDebounce'
import ReportDetailsModal from '@/components/manage/ReportDetailsModal.vue'
import IssueStrikeModal from '@/components/manage/IssueStrikeModal.vue'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const route = useRoute()
const { isBlurred } = usePrivacyMode()
const reportHelpers = useReportHelpers()
const selectedReport = ref<Report | null>(null)
const isStrikeModalOpen = ref(false)
const { debounce } = useDebounce(300)

const pageSize = 20

// Local state for expedition-specific reports
const reports = ref<Report[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const totalCount = ref(0)
const currentPage = ref(1)
const filterType = ref<'all' | 'reports' | 'strikes'>('all')
const searchQuery = ref('')

const expeditionId = computed(() => route.params.id as string)

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize))

const filteredReports = computed(() => {
  let filtered = reports.value
  
  // Apply type filter
  if (filterType.value === 'reports') {
    filtered = filtered.filter(r => !r.is_strike)
  } else if (filterType.value === 'strikes') {
    filtered = filtered.filter(r => r.is_strike)
  }
  
  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(r => 
      r.user_details.first_name.toLowerCase().includes(query) ||
      r.user_details.last_name.toLowerCase().includes(query) ||
      r.user_details.email.toLowerCase().includes(query) ||
      r.description?.toLowerCase().includes(query) ||
      r.reason_display.toLowerCase().includes(query)
    )
  }
  
  return filtered
})

const fetchReports = async (page = 1) => {
  loading.value = true
  error.value = null
  
  try {
    const params: Record<string, any> = { 
      page, 
      page_size: pageSize,
      expedition: expeditionId.value
    }

    const response = await api.get(API_ROUTES.GET_FELLOW_REPORTS, { params })
    reports.value = response.data.results
    totalCount.value = response.data.count
    currentPage.value = page
  } catch (err) {
    console.error('Error fetching expedition reports:', err)
    error.value = 'Failed to fetch reports'
  } finally {
    loading.value = false
  }
}

const setFilter = (type: 'all' | 'reports' | 'strikes') => {
  filterType.value = type
}

const handleSearch = debounce(() => {
  // Search is handled locally via computed property
})

const handlePageChange = (page: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchReports(page)
}

const navigateToReport = (report: Report | FellowReport) => {
  selectedReport.value = report as Report
}

const handleReportUpdate = (updatedReport: Report | FellowReport) => {
  const report = updatedReport as Report
  const index = reports.value.findIndex(r => r.id === report.id)
  if (index !== -1) {
    reports.value[index] = report
  }
  
  if (selectedReport.value?.id === report.id) {
    selectedReport.value = report
  }
}

const handleStrikeSubmitted = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchReports(currentPage.value)
}

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchReports()
})

// Watch for expedition ID changes
watch(
  () => route.params.id,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReports()
  }
)
</script>

